// Where to go after signing in: the page someone was sent away from.
// ProtectedRoute sends signed-out visitors to /login?next=<path> (and router
// state); LoginPage, AuthCallback (Google/Apple) and the terms gate all land
// back on that exact path, query and hash included. Only same-site paths are
// accepted, so ?next can't send anyone off-site.
const KEY = 'bricks.returnTo';
const AUTH_PATHS = /^\/(login|logout|auth(\/|$)|signup|register|reset-password|forgot-password)/;

export function pathOf(loc) {
  if (!loc) return '';
  if (typeof loc === 'string') return loc;
  return `${loc.pathname || ''}${loc.search || ''}${loc.hash || ''}`;
}

export function safeReturnTo(path) {
  if (typeof path !== 'string') return null;
  const p = path.trim();
  if (!p.startsWith('/') || p.startsWith('//') || p.startsWith('/\\') || p.length > 2000) return null;
  if (/[\u0000-\u001f]/.test(p)) return null;
  if (AUTH_PATHS.test(p)) return null;
  if (p === '/') return null;
  return p;
}

export function loginPathFor(loc) {
  const next = safeReturnTo(pathOf(loc));
  return next ? `/login?next=${encodeURIComponent(next)}` : '/login';
}

// The first usable destination: router state, then ?next, then one saved
// before an OAuth round trip, else fallback.
export function returnToFrom(location, fallback = '/') {
  const fromState = safeReturnTo(pathOf(location?.state?.from));
  if (fromState) return fromState;
  const search = location?.search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const fromQuery = safeReturnTo(new URLSearchParams(search || '').get('next'));
  if (fromQuery) return fromQuery;
  return peekReturnTo() || fallback;
}

export function rememberReturnTo(path) {
  const p = safeReturnTo(path);
  try {
    if (p) window.sessionStorage.setItem(KEY, p);
    else window.sessionStorage.removeItem(KEY);
  } catch { /* storage off: the deep link falls back to home */ }
  return p;
}

export function peekReturnTo() {
  try { return safeReturnTo(window.sessionStorage.getItem(KEY)); } catch { return null; }
}

export function takeReturnTo() {
  const p = peekReturnTo();
  try { window.sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  return p;
}
